/**
 * PAPPO — MessageBubble Component
 * Balão de mensagem: enviadas (direita, indigo) vs recebidas (esquerda, cinza).
 * Suporta imagens, reações (emoji) e recibo de leitura.
 */

import { Check, CheckCheck, Reply, Forward } from 'lucide-react';
import type { Message } from '../../types/message';
import { messagePreview } from './messagePreview';
import styles from './MessageBubble.module.css';

/** Reações rápidas disponíveis na afordância de adicionar */
const QUICK_REACTIONS = ['👍', '❤️', '😂'];

interface MessageBubbleProps {
  message: Message;
  /** ID do utilizador atual para determinar sent/received */
  currentUserId: number;
  /** Callback ao reagir (ativa a UI de reações quando definido) */
  onReact?: (messageId: number, emoji: string) => void;
  /** Grupo: mostrar o nome do remetente acima do conteúdo (mensagens recebidas) */
  showSender?: boolean;
  /** Nome do remetente a exibir quando showSender */
  senderName?: string;
  /** Responder (citar) esta mensagem */
  onReply?: (message: Message) => void;
  /** Encaminhar esta mensagem para outra conversa */
  onForward?: (message: Message) => void;
}

/** Formata hora HH:mm */
function formatHour(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MessageBubble({
  message,
  currentUserId,
  onReact,
  showSender = false,
  senderName,
  onReply,
  onForward,
}: MessageBubbleProps) {
  const isSent = message.sender_id === currentUserId;
  // Só renderiza como imagem se o anexo for um upload interno (/uploads/...).
  // Impede que URLs arbitrárias vindas de um cliente malicioso sejam
  // renderizadas como <img>/<a> (defesa em profundidade com a validação no back).
  const isImage =
    message.message_type === 'image' &&
    !!message.attachment_url &&
    message.attachment_url.startsWith('/uploads/');
  // Figurinha: rehospedada em /uploads no envio, mesma guarda da imagem.
  const isSticker =
    message.message_type === 'sticker' &&
    !!message.attachment_url &&
    message.attachment_url.startsWith('/uploads/');
  // Áudio (nota de voz): player + transcrição (o `content` vem transcrito do back).
  const isAudio =
    message.message_type === 'audio' &&
    !!message.attachment_url &&
    message.attachment_url.startsWith('/uploads/');
  // Vídeo: player nativo (<video controls>). Mesma guarda de anexo interno.
  const isVideo =
    message.message_type === 'video' &&
    !!message.attachment_url &&
    message.attachment_url.startsWith('/uploads/');
  const reactions = message.reactions ?? [];

  return (
    <div
      className={`${styles.row} ${isSent ? styles.sent : styles.received}`}
      role="listitem"
    >
      <div
        className={`${styles.bubble} ${isSent ? styles.bubbleSent : styles.bubbleReceived} ${
          isSticker ? styles.bubbleSticker : ''
        }`}
      >
        {showSender && senderName && (
          <span
            style={{
              display: 'block',
              fontSize: '0.72rem',
              fontWeight: 600,
              color: 'var(--accent, #6366f1)',
              marginBottom: 2,
            }}
          >
            {senderName}
          </span>
        )}
        {message.is_forwarded && (
          <span className={styles.forwardedTag}>
            <Forward size={11} /> Encaminhada
          </span>
        )}
        {message.reply_to && (
          <div className={styles.replyQuote}>
            <span className={styles.replyAuthor}>
              {message.reply_to.sender_name || 'Contato'}
            </span>
            <span className={styles.replyText}>
              {messagePreview(message.reply_to)}
            </span>
          </div>
        )}
        {isSticker ? (
          <img
            className={styles.sticker}
            src={message.attachment_url!}
            alt="Figurinha"
          />
        ) : isImage ? (
          <>
            <a
              href={message.attachment_url!}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                className={styles.image}
                src={message.attachment_url!}
                alt={message.content || 'Imagem'}
              />
            </a>
            {message.content && <p className={styles.content}>{message.content}</p>}
          </>
        ) : isAudio ? (
          <>
            <audio
              className={styles.audio}
              controls
              preload="metadata"
              src={message.attachment_url!}
            />
            {message.content && (
              <p className={styles.transcription}>{message.content}</p>
            )}
          </>
        ) : isVideo ? (
          <>
            <video
              className={styles.video}
              controls
              preload="metadata"
              playsInline
              src={message.attachment_url!}
            />
            {message.content && <p className={styles.content}>{message.content}</p>}
          </>
        ) : (
          <p className={styles.content}>{message.content}</p>
        )}

        <time className={styles.time} dateTime={message.created_at}>
          {formatHour(message.created_at)}
          {/* Recibo de leitura: ✓ entregue · ✓✓ lido (só nas minhas mensagens) */}
          {isSent &&
            (message.is_read ? (
              <CheckCheck
                size={14}
                aria-label="Lida"
                style={{ verticalAlign: 'middle', marginLeft: 4 }}
              />
            ) : (
              <Check
                size={14}
                aria-label="Entregue"
                style={{ verticalAlign: 'middle', marginLeft: 4 }}
              />
            ))}
        </time>

        {/* Reações existentes */}
        {reactions.length > 0 && (
          <div className={styles.reactions}>
            {reactions.map((r) => {
              const mine = r.user_ids.includes(currentUserId);
              return (
                <button
                  key={r.emoji}
                  type="button"
                  className={`${styles.reactionPill} ${mine ? styles.reactionMine : ''}`}
                  onClick={() => onReact?.(message.id, r.emoji)}
                  aria-label={`${r.emoji} (${r.count})`}
                >
                  <span>{r.emoji}</span>
                  <span className={styles.reactionCount}>{r.count}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Barra de ações: reagir · responder · encaminhar */}
        {(onReact || onReply || onForward) && (
          <div className={styles.reactBar}>
            {onReact &&
              QUICK_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className={styles.reactAdd}
                  onClick={() => onReact(message.id, emoji)}
                  aria-label={`Reagir com ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            {onReply && (
              <button
                type="button"
                className={styles.actionBtn}
                onClick={() => onReply(message)}
                aria-label="Responder"
                title="Responder"
              >
                <Reply size={15} />
              </button>
            )}
            {onForward && (
              <button
                type="button"
                className={styles.actionBtn}
                onClick={() => onForward(message)}
                aria-label="Encaminhar"
                title="Encaminhar"
              >
                <Forward size={15} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
